import React from "react";
import styled from "@emotion/styled";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {COLORS} from "../../../constants/constants";

export const CSV_IMPORT_STEPS = ["Select a file", "Preview and confirm", "Create orders"];

type CsvImportTimelineProps = {
  // 0-based index of the current step
  currentStep: number;
  // The last step shows as complete once orders are created
  isFinished?: boolean;
};

const CsvImportTimeline = ({currentStep, isFinished = false}: CsvImportTimelineProps) => (
  <Steps aria-label="Import progress">
    {CSV_IMPORT_STEPS.map((label, index) => {
      const isComplete = index < currentStep || (isFinished && index === currentStep);
      const isCurrent = index === currentStep && !isFinished;
      return (
        <StepItem
          key={label}
          aria-current={isCurrent ? "step" : undefined}
          // Focusable from code only: the dialog moves focus here when the step changes
          tabIndex={-1}
          data-testid={`csv-import-step-${index + 1}`}
        >
          <StepMarker isComplete={isComplete} isCurrent={isCurrent}>
            {isComplete ? <FontAwesomeIcon icon={solid("check")} /> : index + 1}
          </StepMarker>
          <StepLabel isActive={isComplete || isCurrent}>{label}</StepLabel>
          {index < CSV_IMPORT_STEPS.length - 1 && <Connector isComplete={index < currentStep} />}
        </StepItem>
      );
    })}
  </Steps>
);

const Steps = styled.ol`
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const StepItem = styled.li`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 10px;

  &:last-of-type {
    flex: 0 0 auto;
  }
`;

const markerBackground = ({isComplete, isCurrent}: {isComplete: boolean; isCurrent: boolean}) => {
  if (isComplete) return COLORS.GREEN_SUCCESS;
  if (isCurrent) return COLORS.BLUE;
  return COLORS.BG_GRAY_LIGHT;
};

const StepMarker = styled.span<{isComplete: boolean; isCurrent: boolean}>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => (props.isComplete || props.isCurrent ? COLORS.WHITE : COLORS.TEXT_GRAY_MEDIUM)};
  background: ${(props) => markerBackground(props)};
  box-shadow: ${(props) => (props.isCurrent ? `0 0 0 4px ${COLORS.BLUE_100}` : "none")};
`;

const StepLabel = styled.span<{isActive: boolean}>`
  flex-shrink: 0;
  font-weight: ${(props) => (props.isActive ? 600 : 400)};
  color: ${(props) => (props.isActive ? COLORS.GRAY_800 : COLORS.TEXT_GRAY_MEDIUM)};
`;

const Connector = styled.span<{isComplete: boolean}>`
  flex: 1;
  height: 2px;
  margin: 0 6px;
  border-radius: 1px;
  background: ${(props) => (props.isComplete ? COLORS.GREEN_SUCCESS : COLORS.COLOR_BORDER_LIGHT)};
`;

export default CsvImportTimeline;
